import type { TaskSchedule } from "../api/requests";

const pad2 = (n: number) => String(n).padStart(2, "0");

const toLocalIso = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}:${pad2(d.getSeconds())}`;

const weekDayConfigToJs = (weekDay: number): number => (weekDay === 7 ? 0 : weekDay);

const applyAnchorTime = (dayDate: Date, anchor: Date): Date =>
  new Date(
    dayDate.getFullYear(),
    dayDate.getMonth(),
    dayDate.getDate(),
    anchor.getHours(),
    anchor.getMinutes(),
    anchor.getSeconds(),
    anchor.getMilliseconds()
  );

const daysInMonth = (year: number, monthIndex: number): number =>
  new Date(year, monthIndex + 1, 0).getDate();

const anchorFromSchedule = (item: TaskSchedule): Date => {
  if (item.nextRunAt) return new Date(item.nextRunAt);
  if (item.createdAt) return new Date(item.createdAt);
  return new Date();
};

const anchorWeekDayConfig = (anchor: Date): number => {
  const js = anchor.getDay();
  return js === 0 ? 7 : js;
};

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function parseScheduleRunLocalParts(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]) - 1,
    d: Number(match[3]),
  };
}

export function computeScheduleRunsInMonth(item: TaskSchedule, year: number, monthIndex: number): string[] {
  const cfg = item.schedule;
  if (!cfg?.period) return [];

  const anchor = anchorFromSchedule(item);
  const todayStart = startOfDay(new Date());
  const nextRunDayStart = item.nextRunAt ? startOfDay(new Date(item.nextRunAt)) : null;
  const dim = daysInMonth(year, monthIndex);
  const out: Date[] = [];

  const pushIfOk = (d: Date) => {
    const dayStart = startOfDay(d);
    if (dayStart < todayStart) return;
    if (nextRunDayStart !== null && dayStart < nextRunDayStart) return;
    out.push(d);
  };

  if (cfg.period === "daily") {
    for (let dom = 1; dom <= dim; dom += 1) {
      const t = applyAnchorTime(new Date(year, monthIndex, dom), anchor);
      pushIfOk(t);
    }
  } else if (cfg.period === "weekly") {
    const wd = cfg.weekDay ?? anchorWeekDayConfig(anchor);
    const targetJs = weekDayConfigToJs(wd);
    for (let dom = 1; dom <= dim; dom += 1) {
      const cand = new Date(year, monthIndex, dom);
      if (cand.getDay() !== targetJs) continue;
      pushIfOk(applyAnchorTime(cand, anchor));
    }
  } else if (cfg.period === "monthly") {
    const raw = cfg.day ?? anchor.getDate();
    let dom: number;
    if (raw >= 31) {
      dom = dim;
    } else {
      dom = Math.min(Math.max(raw, 1), dim);
    }
    const t = applyAnchorTime(new Date(year, monthIndex, dom), anchor);
    pushIfOk(t);
  }

  out.sort((a, b) => a.getTime() - b.getTime());
  return out.map(toLocalIso);
}
